import { useState, useEffect } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiError } from "@/lib/api";
import { DEAL_STAGES, SUBSCRIPTION_PLANS } from "@/lib/dealStages";
import { useAuth } from "@/lib/auth";
import { Sparkles } from "lucide-react";

const inputCls = "rounded-xl h-11";
const selectCls =
  "rounded-xl h-11 w-full px-3 text-sm bg-white border border-gray-300 text-brand-navy focus:border-brand-orange focus:outline-none";

/**
 * Modal that converts an enquiry into an Account + Contact + Deal (Customer).
 *
 * Props: open, onOpenChange, enquiry, team=[], onConverted(result)
 */
export function QualifyDialog({ open, onOpenChange, enquiry, team = [], onConverted }) {
  const { admin } = useAuth();
  const [dealTitle, setDealTitle] = useState("");
  const [value, setValue] = useState("");
  const [plan, setPlan] = useState("essential");
  const [stage, setStage] = useState("qualified");
  const [ownerId, setOwnerId] = useState(admin?.id || "");
  const [expectedClose, setExpectedClose] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && admin?.id && !ownerId) setOwnerId(admin.id);
  }, [open, admin, ownerId]);

  const reset = () => {
    setDealTitle("");
    setValue("");
    setPlan("essential");
    setStage("qualified");
    setOwnerId(admin?.id || "");
    setExpectedClose("");
    setNotes("");
    setSaving(false);
  };

  const handleClose = (next) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!enquiry) return;
    setSaving(true);
    try {
      const payload = {
        deal_value_sgd: Number(value) || 0,
        stage,
        subscription_plan: plan,
      };
      if (dealTitle.trim()) payload.deal_title = dealTitle.trim();
      if (ownerId) payload.owner_id = ownerId;
      if (expectedClose) payload.expected_close_date = expectedClose;
      if (notes.trim()) payload.notes = notes.trim();

      const { data } = await api.post(`/admin/enquiries/${enquiry.id}/qualify`, payload);
      toast.success("Lead converted to Customer");
      onConverted?.(data);
      handleClose(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to convert");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="qualify-dialog">
        <DialogHeader>
          <DialogTitle className="text-brand-navy flex items-center gap-2">
            <Sparkles size={18} className="text-brand-orange" /> Qualify &amp; Convert
          </DialogTitle>
          <DialogDescription>
            Creates a customer entry in the pipeline with the primary contact carried over from this lead.
          </DialogDescription>
        </DialogHeader>

        {enquiry && (
          <div className="bg-brand-cream/50 border border-brand-navy/10 rounded-xl p-3 text-xs text-brand-slate/80">
            <div><span className="text-brand-navy font-semibold">{enquiry.name}</span> · {enquiry.company}</div>
            <div className="text-brand-slate/70">{enquiry.work_email} · {enquiry.industry}</div>
          </div>
        )}

        <form onSubmit={submit} className="space-y-4">
          <Field label="Customer Name">
            <Input
              value={dealTitle}
              onChange={(e) => setDealTitle(e.target.value)}
              placeholder={enquiry?.company || "Customer name"}
              className={inputCls}
              data-testid="qualify-input-title"
            />
          </Field>

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Subscription Plan" required>
              <select
                value={plan}
                onChange={(e) => setPlan(e.target.value)}
                className={selectCls}
                data-testid="qualify-input-plan"
              >
                {SUBSCRIPTION_PLANS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </Field>
            <Field label="Contract Value (SGD)">
              <Input
                type="number"
                min="0"
                step="0.01"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="0.00"
                className={inputCls}
                data-testid="qualify-input-value"
              />
            </Field>
            <Field label="Status" required>
              <select
                value={stage}
                onChange={(e) => setStage(e.target.value)}
                className={selectCls}
                data-testid="qualify-input-stage"
              >
                {DEAL_STAGES.filter((s) => !["won", "lost"].includes(s.value)).map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </Field>
            <Field label="Person In Charge (PIC)" required>
              <select
                value={ownerId}
                onChange={(e) => setOwnerId(e.target.value)}
                className={selectCls}
                data-testid="qualify-input-owner"
              >
                {team.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name || m.email}{m.id === admin?.id ? " (Me)" : ""}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Expected Close Date">
              <Input
                type="date"
                value={expectedClose}
                onChange={(e) => setExpectedClose(e.target.value)}
                className={inputCls}
                data-testid="qualify-input-close"
              />
            </Field>
          </div>

          <Field label="Notes">
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Anything the sales team should know…"
              className="rounded-xl w-full px-3 py-2 text-sm bg-white border border-gray-300 text-brand-navy focus:border-brand-orange focus:outline-none"
              data-testid="qualify-input-notes"
            />
          </Field>

          <DialogFooter>
            <button
              type="button"
              onClick={() => handleClose(false)}
              className="inline-flex items-center gap-2 border border-brand-navy/20 px-5 py-2.5 text-xs font-semibold text-brand-navy hover:bg-brand-cream rounded-full transition-colors"
              data-testid="qualify-cancel-button"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-2 bg-brand-orange text-white px-6 py-2.5 text-xs font-semibold hover:bg-brand-orange-hover rounded-full transition-colors disabled:opacity-60"
              data-testid="qualify-submit-button"
            >
              {saving ? "Converting…" : "Convert to Customer"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label className="text-sm text-brand-navy">
        {label}{required && <span className="text-brand-orange ml-0.5">*</span>}
      </Label>
      {children}
    </div>
  );
}
